import {
  ButtonLoadingEllipsis,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { formatMoney } from "@/utils/StringUtils";
import { AnalyticsTrackingEvent, isShopifyProduct } from "@brandclub/common-ui";
import { useCheckoutUI } from "../../CheckoutUIProvider";
import { useCheckoutCartInfo } from "../../hooks/useCheckoutCartInfo";
import { useLoadingCheckoutIntent } from "../../hooks/useLoadingCheckoutIntent";
import { CheckoutIntent } from "../../types/CheckoutIntent";
import { useEffect, useState } from "react";
import { AsyncCheckoutOrderType } from "@/components/Checkout/types";
import { useTheme } from "@mui/material";
import { useGuestCheckoutEnabled } from "./useGuestCheckout";

export const SubmitCheckoutButton = ({
  showTotal = false,
  text = "Submit my order",
}: {
  text?: string;
  showTotal?: boolean;
}) => {
  const [state, send] = useCheckoutUI();
  const [trackAction] = useTrackActions();

  // @ts-ignore
  const { checkoutIntent } = state.context as {
    checkoutIntent: CheckoutIntent;
  };
  const { experimentEnabled } = useGuestCheckoutEnabled(
    checkoutIntent?.retailerId
  );

  const checkoutIntentId = checkoutIntent?.checkoutIntentId;
  const { loading } = useLoadingCheckoutIntent();
  const { items } = useCheckoutCartInfo();
  const [canPlaceOrder, setCanPlaceOrder] = useState(false);

  const showEnabledSubmitButton = state.value === "ready_to_checkout";

  const checkingOut = state.value === "submit_checkout";

  useEffect(() => {
    if (
      checkoutIntent?.shippingAddress?.address1 &&
      checkoutIntent?.stripePaymentMethod?.id &&
      checkoutIntent?.stripePaymentIntent?.id &&
      (checkoutIntent?.retailerConnection?.username ||
        checkoutIntent?.orderType === AsyncCheckoutOrderType.GUEST ||
        checkoutIntent?.retailerConnectionRequired === false) &&
      (isShopifyProduct(checkoutIntent?.retailerId)
        ? checkoutIntent?.accountInformation?.email
        : true) &&
      (isShopifyProduct(checkoutIntent?.retailerId)
        ? checkoutIntent?.billingAddress?.address1
        : true)
    ) {
      setCanPlaceOrder(true);
    } else {
      setCanPlaceOrder(false);
    }
  }, [checkoutIntent, state.value]);

  const handleSubmitClick = () => {
    trackAction?.(AnalyticsTrackingEvent.CHECKOUT_ORDER_SUBMIT, {
      checkoutIntentId: checkoutIntentId,
      checkoutItems: items || [],
      estimatedTotal: checkoutIntent?.estimatedOrderSummary?.total,
      checkoutPhase: "pre_submission",
      orderType: checkoutIntent?.orderType,
    });
    send({
      type: "SUBMIT_CHECKOUT",
      data: { autoSwitchToGuest: experimentEnabled },
    });
  };

  if (loading) {
    return null;
  }

  return (
    <SolidButton
      className="checkout-solid-button"
      // height={{ md: 50 }}
      disableMaxWidth
      disabled={checkingOut || !canPlaceOrder || !showEnabledSubmitButton}
      onClick={checkingOut ? undefined : handleSubmitClick}
      sx={() => ({
        maxWidth: "unset",
        width: "100%",
        "&:disabled": {
          backgroundColor: "white",
        },
      })}
    >
      {checkingOut ? (
        <>
          Placing your order
          <ButtonLoadingEllipsis baseFontSize={12} customColor={"#DCDDDC"} />
        </>
      ) : state.value === "checking_out" || loading ? (
        <ButtonLoadingEllipsis baseFontSize={16} />
      ) : (
        <>
          {text}
          {showTotal && (
            <>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              {formatMoney(checkoutIntent?.estimatedOrderSummary?.total!)}
            </>
          )}
        </>
      )}
    </SolidButton>
  );
};
